/**
 * OrganizationSubgraph Component
 * Renders a depth-limited ego graph centered on a single organization, shown
 * on the organization detail page. The API (GET /api/graph/subgraph/:id)
 * returns the center node plus all neighbors up to `depth` hops away.
 *
 * Graph structure:
 * - Nodes: The focal organization (center node) plus its 1-hop or 2-hop
 *   neighbors in the Neo4j graph. Each node carries connectionCount and
 *   primarySector for sizing and coloring.
 * - Links: Undirected CONNECTED_TO relationships between the returned
 *   organizations.
 *
 * Force simulation:
 * - Uses react-force-graph-2d defaults: charge (forceManyBody) ~-30,
 *   link distance ~30, center force at [0,0]. No custom force overrides
 *   since the subgraph is small enough that defaults produce a clean layout.
 * - cooldownTicks=100 lets the simulation settle, then onEngineStop
 *   calls zoomToFit to frame all nodes with 30px padding.
 *
 * Key interactions:
 * - Click a neighbor node to navigate to that organization's detail page;
 *   clicking the center node is a no-op (you're already there).
 * - Depth toggle (1 hop / 2 hops) re-fetches the subgraph at the
 *   selected traversal depth.
 * - Graph/Table toggle switches between the force-graph view and a sorted
 *   table listing connected organizations with sector badges.
 * - Hover highlights a node with a dark border and reveals its label.
 * - Center node emphasis: drawn 1.5x larger than neighbors, bold label,
 *   permanent gold (#FFD700) border ring.
 */

import React, { useRef, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, Link } from 'react-router-dom';
import ForceGraph2D from 'react-force-graph-2d';
import {
    Card,
    Spinner,
    Alert,
    ButtonGroup,
    Button,
    Table,
    Badge,
} from 'react-bootstrap';

import { useSubgraphData } from '../hooks/useGraphData';
import { getNodeColor } from '../utils/graphColors';
import { getNodeSize } from '../utils/graphHelpers';

import '../Graph.css';

export default function OrganizationSubgraph({ organizationId, height = 300 }) {
    const navigate = useNavigate();
    const graphRef = useRef();
    const containerRef = useRef();
    const [dimensions, setDimensions] = useState({ width: 400, height });
    const [depth, setDepth] = useState(1);
    const [hoveredNode, setHoveredNode] = useState(null);
    const [viewMode, setViewMode] = useState('graph'); // 'graph' or 'table'

    // Fetch the ego subgraph from GET /api/graph/subgraph/:id?depth=N.
    // When `depth` changes (1 or 2), the hook re-fetches automatically.
    const { graphData, loading, error } = useSubgraphData(
        organizationId,
        depth
    );

    // Get connected organizations (excluding the center org) for table view
    const connectedOrgs = graphData.nodes.filter(
        (node) => node.id !== organizationId
    );

    // Handle responsive sizing
    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth;
                setDimensions({ width, height });
            }
        };

        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, [height]);

    // Handle node click - navigate to that organization
    const handleNodeClick = useCallback(
        (node) => {
            if (node.id !== organizationId) {
                navigate(`/organizations/${node.id}`);
            }
        },
        [navigate, organizationId]
    );

    // --- Custom node rendering (Canvas 2D) ---
    // Draws each node as a filled circle, with special emphasis on the
    // center (focal) organization.
    const nodeCanvasObject = useCallback(
        (node, ctx, globalScale) => {
            const isCenter = node.id === organizationId;
            const isHovered = hoveredNode === node.id;

            // The center node is drawn 1.5x larger than its base size to
            // make it visually dominant in the subgraph.
            const baseSize = getNodeSize();
            const size = isCenter ? baseSize * 1.5 : baseSize;

            // All nodes colored by sector (no toggle in the subgraph view)
            const color = getNodeColor(node, 'sector');

            // Draw filled circle
            ctx.beginPath();
            ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
            ctx.fillStyle = color;
            ctx.fill();

            // Center node: permanent gold ring (3px wide) so it stands out.
            // Hovered neighbor: dark border for feedback.
            if (isCenter) {
                ctx.strokeStyle = '#FFD700';
                ctx.lineWidth = 3;
                ctx.stroke();
            } else if (isHovered) {
                ctx.strokeStyle = '#333';
                ctx.lineWidth = 2;
                ctx.stroke();
            }

            // Label drawing: the center node always shows its label (bold).
            // Neighbors show labels only on hover or when zoomed past 1.5x.
            const label = node.name;
            const fontSize = isCenter
                ? Math.max(12 / globalScale, 4)
                : Math.max(10 / globalScale, 3);

            if (isCenter || isHovered || globalScale > 1.5) {
                ctx.font = `${isCenter ? 'bold ' : ''}${fontSize}px Sans-Serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillStyle = '#333';

                // Truncate long organization names so they don't overlap
                // other nodes. Removes characters from the end until the
                // measured text width fits within maxWidth.
                const maxWidth = 100 / globalScale;
                let displayLabel = label;
                if (ctx.measureText(label).width > maxWidth) {
                    while (
                        ctx.measureText(displayLabel + '...').width >
                            maxWidth &&
                        displayLabel.length > 0
                    ) {
                        displayLabel = displayLabel.slice(0, -1);
                    }
                    displayLabel += '...';
                }

                ctx.fillText(displayLabel, node.x, node.y + size + fontSize);
            }
        },
        [organizationId, hoveredNode]
    );

    // Node hover handling
    const handleNodeHover = useCallback((node) => {
        setHoveredNode(node ? node.id : null);
    }, []);

    // Loading state
    if (loading) {
        return (
            <Card className="border-0 shadow-sm">
                {/* Height set by parent prop -- must be inline */}
                <Card.Body className="p-4 text-center" style={{ height }}>
                    <Spinner animation="border" variant="primary" size="sm" />
                    <p className="mt-2 mb-0 small text-muted">
                        Loading connections...
                    </p>
                </Card.Body>
            </Card>
        );
    }

    // Error state
    if (error) {
        return (
            <Alert variant="warning" className="mb-0">
                <small>{error}</small>
            </Alert>
        );
    }

    // No connections
    if (!graphData.nodes.length || graphData.nodes.length === 1) {
        return (
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-4 text-center text-muted">
                    <div className="subgraph-empty-icon">🔗</div>
                    <small>No connections found for this organization.</small>
                </Card.Body>
            </Card>
        );
    }

    const connectionCount = graphData.nodes.length - 1;

    return (
        <Card className="border-0 shadow-sm organization-subgraph">
            <Card.Header className="bg-white py-2 px-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                        <h6 className="mb-0 text-primary">
                            Network Connections
                        </h6>
                        <small className="text-muted">
                            {connectionCount} connected organization
                            {connectionCount !== 1 ? 's' : ''}
                        </small>
                    </div>
                    <ButtonGroup size="sm">
                        <Button
                            variant={
                                viewMode === 'graph'
                                    ? 'primary'
                                    : 'outline-primary'
                            }
                            onClick={() => setViewMode('graph')}
                            title="Graph view"
                        >
                            Graph
                        </Button>
                        <Button
                            variant={
                                viewMode === 'table'
                                    ? 'primary'
                                    : 'outline-primary'
                            }
                            onClick={() => setViewMode('table')}
                            title="Table view"
                        >
                            Table
                        </Button>
                    </ButtonGroup>
                </div>
                {viewMode === 'graph' && (
                    <div className="d-flex justify-content-end">
                        <ButtonGroup size="sm">
                            <Button
                                variant={
                                    depth === 1
                                        ? 'secondary'
                                        : 'outline-secondary'
                                }
                                onClick={() => setDepth(1)}
                                title="Show direct connections only"
                            >
                                1 hop
                            </Button>
                            <Button
                                variant={
                                    depth === 2
                                        ? 'secondary'
                                        : 'outline-secondary'
                                }
                                onClick={() => setDepth(2)}
                                title="Show connections of connections"
                            >
                                2 hops
                            </Button>
                        </ButtonGroup>
                    </div>
                )}
            </Card.Header>

            {viewMode === 'graph' ? (
                <>
                    <Card.Body className="p-0" ref={containerRef}>
                        {/* ForceGraph2D for the org-centered subgraph.
                        - nodePointerAreaPaint: hit-test area matches the
                          visual size (including center node 1.5x multiplier)
                          plus a 2px tolerance buffer.
                        - cooldownTicks=100: simulation runs 100 ticks then
                          freezes; onEngineStop auto-fits with 30px padding.
                        - No custom forces: the small subgraph lays out well
                          with react-force-graph-2d's default d3-force config. */}
                        <ForceGraph2D
                            ref={graphRef}
                            graphData={graphData}
                            width={dimensions.width}
                            height={height}
                            nodeCanvasObject={nodeCanvasObject}
                            nodePointerAreaPaint={(node, color, ctx) => {
                                const isCenter = node.id === organizationId;
                                const baseSize = getNodeSize();
                                const size = isCenter
                                    ? baseSize * 1.5
                                    : baseSize;
                                ctx.fillStyle = color;
                                ctx.beginPath();
                                ctx.arc(
                                    node.x,
                                    node.y,
                                    size + 2,
                                    0,
                                    2 * Math.PI
                                );
                                ctx.fill();
                            }}
                            linkColor={() => '#E0E0E0'}
                            linkWidth={1}
                            onNodeClick={handleNodeClick}
                            onNodeHover={handleNodeHover}
                            cooldownTicks={100}
                            onEngineStop={() => {
                                if (graphRef.current) {
                                    graphRef.current.zoomToFit(300, 30);
                                }
                            }}
                            enableNodeDrag={true}
                            enableZoomInteraction={true}
                            enablePanInteraction={true}
                        />
                    </Card.Body>
                    <Card.Footer className="bg-white border-top-0 py-2 text-center">
                        <small className="text-muted">
                            Click on a node to view that organization
                        </small>
                    </Card.Footer>
                </>
            ) : (
                // Computed maxHeight from height prop -- must be inline
                <Card.Body
                    className="p-0"
                    style={{ maxHeight: height + 50, overflowY: 'auto' }}
                >
                    <Table hover size="sm" className="mb-0">
                        <thead className="bg-light sticky-top">
                            <tr>
                                <th className="ps-3">Organization</th>
                                <th>Sector</th>
                            </tr>
                        </thead>
                        <tbody>
                            {connectedOrgs
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((org) => (
                                    <tr key={org.id}>
                                        <td className="ps-3">
                                            <Link
                                                to={`/organizations/${org.id}`}
                                                className="text-decoration-none"
                                            >
                                                {org.name}
                                            </Link>
                                        </td>
                                        <td>
                                            <Badge
                                                bg="secondary"
                                                className="fw-normal"
                                            >
                                                {org.primarySector || 'N/A'}
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </Table>
                </Card.Body>
            )}
        </Card>
    );
}

OrganizationSubgraph.propTypes = {
    organizationId: PropTypes.string.isRequired,
    height: PropTypes.number,
};

OrganizationSubgraph.defaultProps = {
    height: 300,
};
